import { skillStore, skillDef, availableSkillIds, SKILL_SLOTS, type SkillId } from "../items/skillStore.js";
import { weaponStore } from "../items/weaponStore.js";
import { drawItemIcon } from "../items/itemArt.js";
import { injectTheme } from "./theme.js";

/**
 * The Skills page (toggled with K): drag a skill from the tray into one of the 4 bar slots (keys
 * 1-4), drag a slot onto another to swap, or right-click a slot to clear it. Mirrors `skillStore`,
 * which the bottom-left skill bar HUD also reflects.
 */

const STYLE_ID = "ghostland-skillpage-style";
const CSS = `
#gl-skillpage-overlay {
  position: fixed; inset: 0; z-index: 30; display: none; align-items: center; justify-content: center;
  background: rgba(4,0,6,.62); font-family: ui-monospace, "SFMono-Regular", Menlo, monospace; color: var(--gl-text);
}
#gl-skillpage-overlay.open { display: flex; }
#gl-skillpage-card {
  padding: 20px 22px 22px; background: var(--gl-panel); border: 1px solid var(--gl-border);
  border-radius: 12px; box-shadow: 0 16px 50px rgba(0,0,0,.7); min-width: 360px;
}
#gl-skillpage-card h1 { margin: 0 0 4px; font-size: 16px; letter-spacing: .16em; font-family: 'Silkscreen', ui-monospace, monospace; }
#gl-skillpage-card .hint { font-size: 11px; color: var(--gl-muted); }
#gl-skillpage-card h2 { font-size: 11px; letter-spacing: .12em; color: var(--gl-muted); margin: 16px 0 8px; }
#gl-skill-slots { display: flex; gap: 10px; }
.gl-sp-slot {
  position: relative; width: 54px; height: 54px; border-radius: 8px;
  background: var(--gl-panel-2); border: 2px solid var(--gl-border);
  display: flex; align-items: center; justify-content: center;
}
.gl-sp-slot.filled { border-color: var(--gl-border-focus); cursor: grab; }
.gl-sp-slot.drop-hint { border-color: var(--gl-accent, #b48cff); box-shadow: 0 0 0 2px rgba(180,140,255,.4) inset; }
.gl-sp-slot canvas { image-rendering: pixelated; width: 36px; height: 36px; pointer-events: none; }
.gl-sp-key { position: absolute; top: 2px; left: 4px; font-size: 11px; font-weight: 700; color: #ffd23f; }
#gl-skill-tray { display: flex; gap: 10px; flex-wrap: wrap; }
.gl-sp-chip {
  display: flex; align-items: center; gap: 6px; cursor: grab;
  background: var(--gl-panel-2); border: 2px solid var(--gl-border); border-radius: 8px; padding: 5px 9px 5px 5px;
}
.gl-sp-chip:hover { border-color: var(--gl-border-focus); }
.gl-sp-chip canvas { image-rendering: pixelated; width: 28px; height: 28px; pointer-events: none; }
.gl-sp-chip span { font-size: 12px; }
`;

function ensureStyle(): void {
  injectTheme();
  if (document.getElementById(STYLE_ID)) return;
  const s = document.createElement("style"); s.id = STYLE_ID; s.textContent = CSS;
  document.head.appendChild(s);
}

let overlay: HTMLDivElement | null = null;
let slotsEl: HTMLDivElement | null = null;
let trayEl: HTMLDivElement | null = null;

function renderSlots(slots: ReadonlyArray<string | null>): void {
  if (!slotsEl) return;
  slotsEl.innerHTML = "";
  for (let i = 0; i < SKILL_SLOTS; i++) {
    const def = skillDef(slots[i]);
    const cell = document.createElement("div");
    cell.className = "gl-sp-slot" + (def ? " filled" : "");
    const key = document.createElement("span"); key.className = "gl-sp-key"; key.textContent = String(i + 1);
    cell.appendChild(key);
    if (def) {
      cell.appendChild(drawItemIcon(def.icon));
      cell.title = `${def.name} — drag to move, right-click to clear`;
      cell.draggable = true;
      cell.addEventListener("dragstart", (e) => e.dataTransfer?.setData("text/plain", `slot:${i}`));
      cell.addEventListener("contextmenu", (e) => { e.preventDefault(); skillStore.setSlot(i, null); });
    }
    cell.addEventListener("dragover", (e) => { e.preventDefault(); cell.classList.add("drop-hint"); });
    cell.addEventListener("dragleave", () => cell.classList.remove("drop-hint"));
    cell.addEventListener("drop", (e) => {
      e.preventDefault(); cell.classList.remove("drop-hint");
      const data = e.dataTransfer?.getData("text/plain") ?? "";
      if (data.startsWith("skill:")) skillStore.setSlot(i, data.slice(6) as SkillId);
      else if (data.startsWith("slot:")) skillStore.swap(i, Number(data.slice(5)));
    });
    slotsEl.appendChild(cell);
  }
}

function renderTray(): void {
  if (!trayEl) return;
  trayEl.innerHTML = "";
  const { hand1, hand2 } = weaponStore.get();
  const available = availableSkillIds(hand1, hand2); // deduped union from equipped gear (+ learned)
  for (const id of available) {
    const def = skillDef(id)!;
    const chip = document.createElement("div");
    chip.className = "gl-sp-chip";
    chip.draggable = true;
    chip.appendChild(drawItemIcon(def.icon));
    const label = document.createElement("span"); label.textContent = def.name;
    chip.appendChild(label);
    chip.addEventListener("dragstart", (e) => e.dataTransfer?.setData("text/plain", `skill:${def.id}`));
    trayEl.appendChild(chip);
  }
}

function ensureOverlay(): HTMLDivElement {
  if (overlay) return overlay;
  ensureStyle();
  overlay = document.createElement("div");
  overlay.id = "gl-skillpage-overlay";
  overlay.innerHTML = `
    <div id="gl-skillpage-card">
      <h1>Skills</h1>
      <div class="hint">drag a skill into a slot · right-click a slot to clear · K or Esc to close</div>
      <h2>BAR SLOTS (keys 1-4)</h2>
      <div id="gl-skill-slots"></div>
      <h2>AVAILABLE</h2>
      <div id="gl-skill-tray"></div>
    </div>`;
  overlay.addEventListener("click", (e) => { if (e.target === overlay) closeSkillPage(); });
  document.body.appendChild(overlay);
  slotsEl = overlay.querySelector("#gl-skill-slots");
  trayEl = overlay.querySelector("#gl-skill-tray");
  renderTray();
  skillStore.subscribe(renderSlots);
  weaponStore.subscribe(renderTray); // re-offer skills as gear changes
  return overlay;
}

export function toggleSkillPage(): void {
  const el = ensureOverlay();
  el.classList.toggle("open");
}
export function closeSkillPage(): void { overlay?.classList.remove("open"); }
export function isSkillPageOpen(): boolean { return overlay?.classList.contains("open") ?? false; }
